import { useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import SegmentManagement from '../components/SegmentManagement';
import { getFullApiUrl } from '../config/api';

interface Campaign {
  campaignId: string;
  name: string;
  status: string;
  discountCode: string;
  targetSegment: string;
  userCount: number;
  discountValue: number;
  createdAt: string;
  emailContent: {
    subject: string;
    body: string;
  };
}

// Component to display target users for a campaign
function TargetUsersList({ segmentId }: { segmentId: string }) {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [segmentId]);

  const loadUsers = async () => {
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      
      const response = await fetch(getFullApiUrl(`/admin/segments/${segmentId}/members`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading users...</p>;
  if (users.length === 0) return <p className="text-sm text-gray-500">No users found</p>;

  return (
    <div className="border rounded-lg p-3 bg-gray-50">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-sm font-medium text-gray-700">
          {users.length} user{users.length !== 1 ? 's' : ''} in segment
        </span>
        <span className="text-gray-500">{expanded ? '▼' : '▶'}</span>
      </button>
      
      {expanded && (
        <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
          {users.map((user, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
              <span className="font-mono text-gray-700">{user.userId}</span>
              {user.intentScore && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  Score: {user.intentScore}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CampaignManagement() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'segments'>('campaigns');
  const [triggeringWorkflow, setTriggeringWorkflow] = useState(false);
  const [workflowStatus, setWorkflowStatus] = useState<string | null>(null);

  const pollWorkflowStatus = async (executionArn: string, token: string) => {
    const maxAttempts = 60; // 5 minutes max (5 second intervals)
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(getFullApiUrl(`/admin/campaigns/workflow-status?executionArn=${encodeURIComponent(executionArn)}`), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'SUCCEEDED') {
            setWorkflowStatus('✅ Workflow completed successfully! Campaigns and discounts updated.');
            setTriggeringWorkflow(false);
            loadCampaigns(); // Refresh campaign list
            return;
          } else if (data.status === 'FAILED' || data.status === 'TIMED_OUT' || data.status === 'ABORTED') {
            setWorkflowStatus(`❌ Workflow ${data.status.toLowerCase()}`);
            setTriggeringWorkflow(false);
            return;
          } else if (data.status === 'RUNNING') {
            setWorkflowStatus(`⏳ Running... (${attempts * 5}s elapsed)`);
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 5000); // Poll every 5 seconds
            } else {
              setWorkflowStatus('⚠️ Workflow still running (check CloudWatch for details)');
              setTriggeringWorkflow(false);
            }
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
        setWorkflowStatus('⚠️ Lost connection to workflow status');
        setTriggeringWorkflow(false);
      }
    };
    
    poll();
  };

  const triggerWorkflow = async () => {
    if (!confirm('This will trigger segmentation, campaign evaluation, and discount generation. This may take 2-5 minutes. Continue?')) return;
    
    setTriggeringWorkflow(true);
    setWorkflowStatus('Starting workflow...');
    
    try {
      const token = await authService.getToken();
      if (!token) return;
      const response = await fetch(getFullApiUrl('/admin/campaigns/trigger-workflow'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkflowStatus('⏳ Workflow started, monitoring progress...');
        
        // Start polling for status
        pollWorkflowStatus(data.executionArn, token);
      } else {
        const error = await response.json();
        setWorkflowStatus(`❌ Failed to start: ${error.error || 'Unknown error'}`);
        setTriggeringWorkflow(false);
      }
    } catch (error) {
      console.error('Error triggering workflow:', error);
      setWorkflowStatus('❌ Error triggering workflow');
      setTriggeringWorkflow(false);
    }
  };

  const [campaignFilter, setCampaignFilter] = useState<'pending' | 'approved' | 'all'>('all');
  const [solutionFilter, setSolutionFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [discountStatus, setDiscountStatus] = useState<'active' | 'inactive'>('inactive');
  const [togglingDiscount, setTogglingDiscount] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<'active' | 'inactive'>('active');
  const [togglingCampaign, setTogglingCampaign] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, [solutionFilter]);

  const loadCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      // Get auth token
      let token = authService.getToken();
      
      if (!token) {
        try {
          const session = await fetchAuthSession();
          token = session.tokens?.idToken?.toString() || null;
          if (token) {
            authService.setToken(token);
          }
        } catch (error) {
          console.error('Failed to get session token:', error);
        }
      }
      
      if (!token) {
        setError('Authentication required');
        return;
      }
      
      const params = campaignFilter !== 'all' ? `?status=${campaignFilter === 'pending' ? 'pending_approval' : 'approved'}` : '';
      const solutionParam = solutionFilter !== 'all' ? `${params ? '&' : '?'}solutionId=${solutionFilter}` : '';
      const response = await fetch(getFullApiUrl(`/admin/campaigns${params}${solutionParam}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const allCamps = data.campaigns || [];
      setAllCampaigns(allCamps);
      setCampaigns(allCamps);
    } catch (err: any) {
      console.error('Load campaigns error:', err);
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = (tab: 'pending' | 'approved' | 'all') => {
    if (tab === 'all') {
      setCampaigns(allCampaigns);
    } else if (tab === 'pending') {
      setCampaigns(allCampaigns.filter(c => c.status === 'pending_approval'));
    } else {
      // Approved tab shows: approved, sent, sending, failed statuses
      setCampaigns(allCampaigns.filter(c => 
        c.status === 'approved' || 
        c.status === 'sent' || 
        c.status === 'sending' ||
        c.status === 'failed' ||
        c.status === 'partially_sent'
      ));
    }
  };

  const refreshUserCount = async () => {
    if (!selectedCampaign) return;
    
    setLoading(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch(getFullApiUrl(`/admin/campaigns/${selectedCampaign.campaignId}/refresh`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to refresh user count');
      
      const data = await response.json();
      
      // Update selected campaign with new user count
      setSelectedCampaign({
        ...selectedCampaign,
        userCount: data.userCount
      });
      
      // Update in campaigns list
      setCampaigns(campaigns.map(c => 
        c.campaignId === selectedCampaign.campaignId 
          ? { ...c, userCount: data.userCount }
          : c
      ));
      
      alert(`User count refreshed: ${data.userCount} users in segment`);
    } catch (err: any) {
      alert('Failed to refresh: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const approveCampaign = async (campaignId: string) => {
    setLoading(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      
      if (!token) {
        alert('Authentication required');
        return;
      }
      
      const body: any = {};
      if (testMode && testEmails.trim()) {
        body.testMode = true;
        body.testEmails = testEmails.split(',').map(e => e.trim()).filter(e => e);
      }
      
      const response = await fetch(getFullApiUrl(`/admin/campaigns/${campaignId}/approve`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Failed to approve');
      
      if (testMode) {
        alert(`✅ Test emails sent successfully to ${body.testEmails.length} address(es)!\n\n` +
              `The campaign remains in "Pending" status.\n` +
              `To send to all ${selectedCampaign?.userCount || 0} users in the segment, approve again without test mode.`);
      } else {
        alert(`✅ Campaign approved!\n\nEmails are being sent to ${selectedCampaign?.userCount || 0} users in the ${selectedCampaign?.targetSegment} segment.`);
      }
      
      setShowApproveModal(false);
      setTestMode(false);
      setTestEmails('');
      loadCampaigns();
      setSelectedCampaign(null);
    } catch (err: any) {
      alert('Failed to approve campaign: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const rejectCampaign = async (campaignId: string) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    setLoading(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      
      if (!token) {
        alert('Authentication required');
        return;
      }
      
      const response = await fetch(getFullApiUrl(`/admin/campaigns/${campaignId}/reject`), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });
      if (!response.ok) throw new Error('Failed to reject');
      alert('Campaign rejected');
      loadCampaigns();
      setSelectedCampaign(null);
    } catch (err: any) {
      alert('Failed to reject campaign: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDiscountStatus = async (code: string) => {
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      if (!token) return;

      const response = await fetch(getFullApiUrl(`/admin/discounts/${code}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDiscountStatus(data.discount?.status || 'inactive');
      }
    } catch (err) {
      console.error('Failed to load discount status:', err);
    }
  };

  const toggleDiscount = async () => {
    if (!selectedCampaign) return;
    
    const newStatus = discountStatus === 'active' ? 'inactive' : 'active';
    if (!confirm(`${newStatus === 'active' ? 'Enable' : 'Disable'} discount ${selectedCampaign.discountCode}?`)) return;

    setTogglingDiscount(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch(getFullApiUrl(`/admin/discounts/${selectedCampaign.discountCode}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle discount');
      
      setDiscountStatus(newStatus);
      alert(`Discount ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!`);
    } catch (err: any) {
      alert('Failed to toggle discount: ' + err.message);
    } finally {
      setTogglingDiscount(false);
    }
  };

  const toggleCampaign = async () => {
    if (!selectedCampaign) return;
    
    const newStatus = campaignStatus === 'active' ? 'inactive' : 'active';
    if (!confirm(`${newStatus === 'active' ? 'Enable' : 'Disable'} campaign "${selectedCampaign.name}"?\n\n${newStatus === 'inactive' ? 'When disabled, this campaign will not send emails.' : 'When enabled, this campaign can send emails if approved.'}`)) return;

    setTogglingCampaign(true);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch(getFullApiUrl(`/admin/campaigns/${selectedCampaign.campaignId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle campaign');
      
      setCampaignStatus(newStatus);
      alert(`Campaign ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!`);
    } catch (err: any) {
      alert('Failed to toggle campaign: ' + err.message);
    } finally {
      setTogglingCampaign(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Campaign Management</h1>

      {/* Main Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 ${activeTab === 'campaigns' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
        >
          Campaigns
        </button>
        <button
          onClick={() => setActiveTab('segments')}
          className={`px-4 py-2 ${activeTab === 'segments' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
        >
          Segments
        </button>
      </div>

      {activeTab === 'segments' ? (
        <SegmentManagement />
      ) : (
        <>
          {/* Action Buttons */}
          <div className="mb-4">
            <button
              onClick={triggerWorkflow}
              disabled={triggeringWorkflow}
              className={`px-4 py-2 rounded-lg text-white ${
                triggeringWorkflow 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {triggeringWorkflow ? '⏳ Processing...' : '🚀 Trigger Campaign Workflow'}
            </button>
            {workflowStatus && (
              <div className={`mt-2 p-3 rounded-lg ${
                workflowStatus.includes('✅') 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {workflowStatus}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Triggers: User segmentation → Campaign evaluation → Discount generation
            </p>
          </div>
          
          {/* Solution Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Solution</label>
            <select
              value={solutionFilter}
              onChange={(e) => {
                setSolutionFilter(e.target.value);
                loadCampaigns();
              }}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Solutions</option>
              <option value="aws-solution-finder">AWS Solution Finder (FasII)</option>
            </select>
          </div>

          {/* Campaign Filter Tabs */}
          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => { setCampaignFilter('pending'); filterCampaigns('pending'); }}
              className={`px-4 py-2 ${campaignFilter === 'pending' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
            >
              Pending ({allCampaigns.filter(c => c.status === 'pending_approval').length})
            </button>
            <button
              onClick={() => { setCampaignFilter('approved'); filterCampaigns('approved'); }}
              className={`px-4 py-2 ${campaignFilter === 'approved' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
            >
              Approved ({allCampaigns.filter(c => c.status === 'approved').length})
            </button>
            <button
              onClick={() => { setCampaignFilter('all'); filterCampaigns('all'); }}
              className={`px-4 py-2 ${campaignFilter === 'all' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
            >
              All ({allCampaigns.length})
            </button>
          </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && <div className="text-center py-8">Loading...</div>}

      {/* Campaign List */}
      {!loading && !selectedCampaign && (
        <div className="grid gap-4">
          {campaigns.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No campaigns found</div>
          ) : (
            campaigns.map(campaign => (
              <div
                key={campaign.campaignId}
                className="border rounded-lg p-4 hover:shadow-lg cursor-pointer"
                onClick={() => {
                  setSelectedCampaign(campaign);
                  loadDiscountStatus(campaign.discountCode);
                  setCampaignStatus(campaign.status === 'active' || campaign.status === 'approved' || campaign.status === 'sent' ? 'active' : 'inactive');
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold">{campaign.name}</h3>
                    <p className="text-gray-600">
                      {campaign.targetSegment} • {campaign.userCount} users
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      campaign.status === 'pending_approval'
                        ? 'bg-yellow-100 text-yellow-800'
                        : campaign.status === 'sent'
                        ? 'bg-green-100 text-green-800'
                        : campaign.status === 'sending'
                        ? 'bg-blue-100 text-blue-800'
                        : campaign.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : campaign.status === 'partially_sent'
                        ? 'bg-orange-100 text-orange-800'
                        : campaign.status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {campaign.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <div className="mt-2 flex gap-4 text-sm text-gray-600">
                  <span>Code: <strong>{campaign.discountCode}</strong></span>
                  <span>Discount: <strong>{campaign.discountValue}% off</strong></span>
                  <span>Created: {new Date(campaign.createdAt).toLocaleDateString()} 
                    <span className="text-gray-500 ml-1">
                      ({Math.floor((Date.now() - new Date(campaign.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days ago)
                    </span>
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Campaign Detail View */}
      {selectedCampaign && (
        <div className="border rounded-lg p-6">
          <button
            onClick={() => setSelectedCampaign(null)}
            className="mb-4 text-blue-600 hover:underline"
          >
            ← Back to list
          </button>

          <h2 className="text-2xl font-bold mb-4">{selectedCampaign.name}</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm text-gray-600">Campaign Status</label>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded text-sm font-medium ${
                  campaignStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {campaignStatus === 'active' ? '✓ Active' : '✗ Inactive'}
                </span>
                <button
                  onClick={toggleCampaign}
                  disabled={togglingCampaign}
                  className={`px-3 py-1 text-xs rounded ${
                    campaignStatus === 'active' 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  } disabled:opacity-50`}
                  title={`${campaignStatus === 'active' ? 'Disable' : 'Enable'} campaign`}
                >
                  {togglingCampaign ? '...' : (campaignStatus === 'active' ? 'Disable Campaign' : 'Enable Campaign')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {campaignStatus === 'active' ? 'Campaign can send emails when approved' : 'Campaign will not send emails'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Original Status</label>
              <p className="font-semibold">{selectedCampaign.status}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Discount Code & Status</label>
              <div className="flex items-center gap-3">
                <p className="font-semibold">{selectedCampaign.discountCode}</p>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  discountStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  Discount: {discountStatus}
                </span>
                <button
                  onClick={toggleDiscount}
                  disabled={togglingDiscount}
                  className={`px-3 py-1 text-xs rounded ${
                    discountStatus === 'active' 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  } disabled:opacity-50`}
                  title={`${discountStatus === 'active' ? 'Disable' : 'Enable'} discount code for checkout`}
                >
                  {togglingDiscount ? '...' : (discountStatus === 'active' ? 'Disable Discount' : 'Enable Discount')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {discountStatus === 'active' ? '✓ Customers can use this code at checkout' : '✗ Code cannot be used at checkout'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Target Segment</label>
              <p className="font-semibold">{selectedCampaign.targetSegment}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">User Count</label>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{selectedCampaign.userCount}</p>
                <button
                  onClick={refreshUserCount}
                  disabled={loading}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded disabled:opacity-50"
                  title="Refresh current segment user count"
                >
                  {loading ? '...' : '🔄 Refresh'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Created {Math.floor((Date.now() - new Date(selectedCampaign.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days ago
                {Math.floor((Date.now() - new Date(selectedCampaign.createdAt).getTime()) / (1000 * 60 * 60 * 24)) > 3 && 
                  <span className="text-orange-600 ml-1">⚠️ Consider refreshing</span>
                }
              </p>
            </div>
            <div className="col-span-2">
              <label className="text-sm text-gray-600 mb-2 block">Target Users</label>
              <TargetUsersList segmentId={selectedCampaign.targetSegment} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Discount Value</label>
              <p className="font-semibold">{selectedCampaign.discountValue}% off</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Created</label>
              <p className="font-semibold">{new Date(selectedCampaign.createdAt).toLocaleString()}</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Email Preview</h3>
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
              {/* Email Header */}
              <div className="bg-gray-100 px-4 py-3 border-b">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <span className="font-medium">From:</span>
                  <span>CloudNestle &lt;noreply@cloudnestle.com&gt;</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <span className="font-medium">To:</span>
                  <span>User ({selectedCampaign.userCount} recipients)</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Subject:</span>
                  <span className="font-semibold text-gray-900">{selectedCampaign.emailContent.subject}</span>
                </div>
              </div>
              
              {/* Email Body */}
              <div className="p-6 bg-white">
                <div className="prose max-w-none">
                  <div 
                    className="text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: selectedCampaign.emailContent.body
                        .replace(/\n/g, '<br/>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/{{userName}}/g, '<span class="bg-yellow-100 px-1 rounded">John Doe</span>')
                        .replace(/{{discountCode}}/g, `<span class="bg-blue-100 px-1 rounded font-mono">${selectedCampaign.discountCode}</span>`)
                        .replace(/{{discountValue}}/g, `<span class="bg-green-100 px-1 rounded">${selectedCampaign.discountValue}%</span>`)
                        .replace(/{{expiryDate}}/g, '<span class="bg-orange-100 px-1 rounded">7 Feb 2026, 2:00 am</span>')
                        .replace(/{{upgradeLink}}/g, '<a href="#" class="text-blue-600 underline">Upgrade Now</a>')
                    }}
                  />
                </div>
              </div>
              
              {/* Variable Legend */}
              <div className="bg-gray-50 px-4 py-3 border-t">
                <p className="text-xs text-gray-600 mb-2 font-medium">Variables used:</p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-yellow-100 px-2 py-1 rounded">userName</span>
                  <span className="bg-blue-100 px-2 py-1 rounded">discountCode</span>
                  <span className="bg-green-100 px-2 py-1 rounded">discountValue</span>
                  <span className="bg-orange-100 px-2 py-1 rounded">expiryDate</span>
                  <span className="bg-purple-100 px-2 py-1 rounded">upgradeLink</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            {selectedCampaign.status === 'pending_approval' && (
              <>
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Approve Campaign
                </button>
                <button
                  onClick={() => rejectCampaign(selectedCampaign.campaignId)}
                  disabled={loading}
                  className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Reject Campaign
                </button>
              </>
            )}
            
            {(selectedCampaign.status === 'approved' || selectedCampaign.status === 'sent' || selectedCampaign.status === 'sending') && (
              <button
                onClick={() => window.location.href = `/admin/campaigns/${selectedCampaign.campaignId}/analytics`}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                View Analytics
              </button>
            )}
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Approve Campaign</h3>
            
            {/* Test Mode Toggle */}
            <div className="mb-6 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  className="w-5 h-5"
                />
                <div>
                  <span className="font-medium text-blue-900">Test Mode</span>
                  <p className="text-sm text-blue-700">Send to test emails only (recommended for first approval)</p>
                </div>
              </label>
            </div>

            {testMode && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <p className="font-medium text-yellow-900 mb-1">Test Mode Active</p>
                    <p className="text-sm text-yellow-800">
                      Emails will ONLY be sent to the addresses below. The {selectedCampaign.userCount} users in the segment will NOT receive emails.
                    </p>
                  </div>
                </div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Test Email Addresses (comma-separated)
                </label>
                <input
                  type="text"
                  value={testEmails}
                  onChange={(e) => setTestEmails(e.target.value)}
                  placeholder="admin@cloudnestle.com, test@cloudnestle.com"
                  className="w-full px-3 py-2 border border-yellow-300 rounded focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            )}

            {!testMode && (
              <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-2xl">🚀</span>
                  <div>
                    <p className="font-medium text-orange-900 mb-1">Production Mode</p>
                    <p className="text-sm text-orange-800">
                      This will send emails to <strong>{selectedCampaign.userCount} users</strong> in the {selectedCampaign.targetSegment} segment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Preview in Modal */}
            <div className="mb-4">
              <h4 className="font-semibold mb-2 text-gray-700">Email Preview:</h4>
              <div className="border rounded-lg overflow-hidden bg-gray-50 max-h-60 overflow-y-auto">
                <div className="p-3 bg-white border-b">
                  <p className="text-sm"><strong>Subject:</strong> {selectedCampaign.emailContent.subject}</p>
                </div>
                <div className="p-3 text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedCampaign.emailContent.body.substring(0, 300)}...
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => approveCampaign(selectedCampaign.campaignId)}
                disabled={loading || (testMode && !testEmails.trim())}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Processing...' : (testMode ? '📧 Send Test Email' : '🚀 Approve & Send to All')}
              </button>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setTestMode(false);
                  setTestEmails('');
                }}
                disabled={loading}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
